import type { LiveNote } from "../types/generated/live";

// MapNote::LIMIT.
export const NOTE_LIMIT = 200;
// MapNote::MARK_LENGTH, in graphemes, and MapNote::TAGS in the order they are stored.
export const MARK_LENGTH = 3;
export const NOTE_TAGS = ["S", "E", "T", "A", "F"];
// A mark's disc and its hit, in screen pixels: it keeps its size whatever the zoom.
export const NOTE_RADIUS = 15;
export const NOTE_HIT = 18;

// The highest whole number among the scene's marks, plus one; past 999 (a mark is three graphemes), the smallest one not in use.
export function nextMark(notes: Pick<LiveNote, "mark">[]): string {
	const numbers = notes.map(({ mark }) => (/^\d+$/.test(mark) ? Number(mark) : 0));
	const next = Math.max(0, ...numbers) + 1;
	if (next < 10 ** MARK_LENGTH) return String(next);
	const used = new Set(numbers);
	let free = 1;
	while (used.has(free)) free++;
	return String(free);
}

// The topmost mark within reach of an image point: the latest in the list.
export function hitNote<T extends Pick<LiveNote, "x" | "y">>(notes: T[], zoom: number, point: { x: number; y: number }): T | null {
	const reach = NOTE_HIT / zoom;
	return notes.findLast((note) => Math.hypot(note.x - point.x, note.y - point.y) <= reach) ?? null;
}

// The first line of a note's text is its title; the rest reads as a single line under it.
export function noteLines(text: string): { title: string; rest: string } {
	const [title = "", ...lines] = text.split("\n");
	return { title: title.trim(), rest: lines.join(" ").replace(/\s+/g, " ").trim() };
}

// A mark cut to its first graphemes: a number, a letter or an emoji, which may be several code points.
export function clampMark(mark: string): string {
	const parts = typeof Intl.Segmenter === "function" ? [...new Intl.Segmenter().segment(mark)].map(({ segment }) => segment) : [...mark];
	return parts.slice(0, MARK_LENGTH).join("");
}
